/**
 * Performance Monitoring Hook
 * Real-time latency tracking with WebSocket and REST probe integration
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import {
  PerfMonitorState,
  PerfMetrics,
  PerfAlert,
  GatewayProbeResult,
  UsePerfMonitorOptions,
  UsePerfMonitorReturn,
  PROBE_CONFIG,
  createInitialMetrics,
  updateMetricWithSample,
  generateAlertId,
} from '../types/perf.types';

// Service URLs - read from environment variables with localhost fallback for development
const API_GATEWAY_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const ORDERS_WS_URL = import.meta.env.VITE_WS_URL || 'http://localhost:3003';

/**
 * Performance Monitor Hook
 * Probes API Gateway, services, and WebSocket latency at configurable intervals
 */
export function usePerfMonitor(options: UsePerfMonitorOptions = {}): UsePerfMonitorReturn {
  const { autoStart = true, onAlert, enableBrowserNotifications = true } = options;

  // State
  const [state, setState] = useState<PerfMonitorState>({
    metrics: createInitialMetrics(),
    isConnected: false,
    isProbing: false,
    lastProbeTime: null,
    lastError: null,
    alerts: [],
    connectionAttempts: 0,
  });

  // Refs
  const socketRef = useRef<Socket | null>(null);
  const probeIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMonitoringRef = useRef(false);
  const isProbingRef = useRef(false);
  const onAlertRef = useRef(onAlert);

  // Keep onAlert ref updated
  useEffect(() => {
    onAlertRef.current = onAlert;
  }, [onAlert]);

  /**
   * Send browser notification
   */
  const sendBrowserNotification = useCallback(
    (alert: PerfAlert) => {
      if (!enableBrowserNotifications) return;
      if (Notification.permission !== 'granted') return;

      const icon = alert.level === 'critical' ? '🔴' : '🟡';
      new Notification(`${icon} Perf Alert: ${alert.displayName}`, {
        body: alert.message,
        tag: alert.id,
        requireInteraction: alert.level === 'critical',
      });
    },
    [enableBrowserNotifications],
  );

  /**
   * Add alert to state and trigger callbacks
   */
  const addAlert = useCallback(
    (alert: PerfAlert) => {
      setState((prev) => ({
        ...prev,
        alerts: [alert, ...prev.alerts].slice(0, 50), // Keep last 50 alerts
      }));

      onAlertRef.current?.(alert);
      sendBrowserNotification(alert);
    },
    [sendBrowserNotification],
  );

  /**
   * Check metric for alert conditions
   */
  const checkForAlert = useCallback(
    (
      metric: {
        name: string;
        displayName: string;
        alertLevel: string;
        current: number | null;
        consecutiveYellows: number;
        consecutiveReds: number;
      },
      prevAlertLevel: string,
    ) => {
      if (metric.alertLevel === 'none' || metric.alertLevel === prevAlertLevel) {
        return;
      }

      const alert: PerfAlert = {
        id: generateAlertId(),
        timestamp: Date.now(),
        level: metric.alertLevel as 'warning' | 'critical',
        metric: metric.name,
        displayName: metric.displayName,
        message:
          metric.alertLevel === 'critical'
            ? `${metric.displayName} has ${metric.consecutiveReds} consecutive red readings (>${(PROBE_CONFIG.INTERVAL_MS * 2) / 1000}s of high latency)`
            : `${metric.displayName} has ${metric.consecutiveYellows} consecutive yellow readings`,
        currentMs: metric.current || 0,
        consecutiveCount:
          metric.alertLevel === 'critical' ? metric.consecutiveReds : metric.consecutiveYellows,
        acknowledged: false,
      };

      addAlert(alert);
    },
    [addAlert],
  );

  /**
   * Probe WebSocket latency using perf:ping/pong
   */
  const probeWebSocketLatency = useCallback(async (): Promise<number | null> => {
    const socket = socketRef.current;
    if (!socket || !socket.connected) return null;

    return new Promise((resolve) => {
      const clientTimestamp = Date.now();
      const timeout = setTimeout(() => resolve(null), 5000);

      socket.once(
        'perf:pong',
        (data: {
          clientTimestamp: number;
          serverTimestamp: number;
          serverProcessingMs: number;
        }) => {
          clearTimeout(timeout);
          const rtt = Date.now() - data.clientTimestamp;
          resolve(rtt);
        },
      );

      socket.emit('perf:ping', {
        clientTimestamp,
        clientId: 'perf-monitor',
      });
    });
  }, []);

  /**
   * Probe API Gateway and all services
   */
  const probeGateway = useCallback(async (): Promise<GatewayProbeResult | null> => {
    try {
      const response = await fetch(`${API_GATEWAY_URL}/api/v1/perf/probe`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[PerfMonitor] Gateway probe failed:', error);
      return null;
    }
  }, []);

  /**
   * Execute full probe cycle
   */
  const probeNow = useCallback(async () => {
    if (isProbingRef.current) return;
    isProbingRef.current = true;

    setState((prev) => ({ ...prev, isProbing: true, lastError: null }));

    try {
      const timestamp = Date.now();

      // Parallel probes
      const [wsLatency, gatewayResult] = await Promise.all([
        probeWebSocketLatency(),
        probeGateway(),
      ]);

      setState((prev) => {
        const newMetrics = { ...prev.metrics };
        const prevAlertLevels: Record<string, string> = {};

        // Store previous alert levels for comparison
        Object.keys(newMetrics).forEach((key) => {
          prevAlertLevels[key] = newMetrics[key as keyof PerfMetrics].alertLevel;
        });

        // Update WebSocket RTT
        if (wsLatency !== null) {
          newMetrics.wsRoundTrip = updateMetricWithSample(
            newMetrics.wsRoundTrip,
            wsLatency,
            timestamp,
          );
        }

        // Update gateway and service metrics
        if (gatewayResult) {
          // Gateway processing
          newMetrics.gateway = updateMetricWithSample(
            newMetrics.gateway,
            gatewayResult.gatewayProcessingMs,
            timestamp,
          );

          // Auth DB
          if (gatewayResult.breakdown.auth_db !== null) {
            newMetrics.authDb = updateMetricWithSample(
              newMetrics.authDb,
              gatewayResult.breakdown.auth_db,
              timestamp,
            );
          }

          // Catalog DB
          if (gatewayResult.breakdown.catalog_db !== null) {
            newMetrics.catalogDb = updateMetricWithSample(
              newMetrics.catalogDb,
              gatewayResult.breakdown.catalog_db,
              timestamp,
            );
          }

          // Orders DB
          if (gatewayResult.breakdown.orders_db !== null) {
            newMetrics.ordersDb = updateMetricWithSample(
              newMetrics.ordersDb,
              gatewayResult.breakdown.orders_db,
              timestamp,
            );
          }

          // Redis
          if (gatewayResult.breakdown.redis !== null) {
            newMetrics.redis = updateMetricWithSample(
              newMetrics.redis,
              gatewayResult.breakdown.redis,
              timestamp,
            );
          }

          // NATS JetStream
          if (gatewayResult.breakdown.nats !== null) {
            newMetrics.nats = updateMetricWithSample(
              newMetrics.nats,
              gatewayResult.breakdown.nats,
              timestamp,
            );
          }

          // Total latency
          newMetrics.totalLatency = updateMetricWithSample(
            newMetrics.totalLatency,
            gatewayResult.breakdown.total,
            timestamp,
          );
        }

        // Check for alerts on each metric
        Object.keys(newMetrics).forEach((key) => {
          const metric = newMetrics[key as keyof PerfMetrics];
          checkForAlert(metric, prevAlertLevels[key]);
        });

        isProbingRef.current = false;
        return {
          ...prev,
          metrics: newMetrics,
          isProbing: false,
          lastProbeTime: timestamp,
          lastError: gatewayResult === null && wsLatency === null ? 'All probes failed' : null,
        };
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('[PerfMonitor] Probe error:', errorMessage);
      isProbingRef.current = false;

      setState((prev) => ({
        ...prev,
        isProbing: false,
        lastError: errorMessage,
      }));
    }
  }, [probeWebSocketLatency, probeGateway, checkForAlert]);

  /**
   * Initialize WebSocket connection
   */
  const initWebSocket = useCallback(() => {
    if (socketRef.current?.connected) return;

    const socket = io(`${ORDERS_WS_URL}/perf`, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[PerfMonitor] WebSocket connected');
      setState((prev) => ({
        ...prev,
        isConnected: true,
        connectionAttempts: 0,
      }));
    });

    socket.on('disconnect', (reason) => {
      console.log('[PerfMonitor] WebSocket disconnected:', reason);
      setState((prev) => ({
        ...prev,
        isConnected: false,
      }));
    });

    socket.on('connect_error', (error) => {
      console.error('[PerfMonitor] WebSocket connection error:', error.message);
      setState((prev) => ({
        ...prev,
        isConnected: false,
        connectionAttempts: prev.connectionAttempts + 1,
      }));
    });
  }, []);

  /**
   * Start monitoring
   */
  const startMonitoring = useCallback(() => {
    if (isMonitoringRef.current) return;

    console.log('[PerfMonitor] Starting monitoring...');
    isMonitoringRef.current = true;

    // Initialize WebSocket
    initWebSocket();

    // Initial probe
    probeNow();

    // Set up interval
    probeIntervalRef.current = setInterval(() => {
      probeNow();
    }, PROBE_CONFIG.INTERVAL_MS);
  }, [initWebSocket, probeNow]);

  /**
   * Stop monitoring
   */
  const stopMonitoring = useCallback(() => {
    console.log('[PerfMonitor] Stopping monitoring...');
    isMonitoringRef.current = false;

    // Clear interval
    if (probeIntervalRef.current) {
      clearInterval(probeIntervalRef.current);
      probeIntervalRef.current = null;
    }

    // Disconnect WebSocket
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setState((prev) => ({
      ...prev,
      isConnected: false,
    }));
  }, []);

  /**
   * Acknowledge alert
   */
  const acknowledgeAlert = useCallback((alertId: string) => {
    setState((prev) => ({
      ...prev,
      alerts: prev.alerts.map((a) => (a.id === alertId ? { ...a, acknowledged: true } : a)),
    }));
  }, []);

  /**
   * Clear all alerts
   */
  const clearAlerts = useCallback(() => {
    setState((prev) => ({
      ...prev,
      alerts: [],
    }));
  }, []);

  // Auto-start on mount - use refs to avoid dependency changes
  useEffect(() => {
    if (autoStart) {
      startMonitoring();
    }

    return () => {
      stopMonitoring();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  return {
    state,
    startMonitoring,
    stopMonitoring,
    probeNow,
    acknowledgeAlert,
    clearAlerts,
  };
}
